import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { catchError, map, Observable, of, take } from 'rxjs';
import * as Sentry from '@sentry/angular';
import { SessionService } from '../core/session.service';
import { SAML_LOGIN_COMPANY_ID } from '../shared/constants/storage-keys.constants';
import {
  safeStorageGet,
  safeStorageRemove,
} from '../shared/utils/safe-storage';

export const SamlRedirectGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
):
  | Observable<boolean | UrlTree>
  | Promise<boolean | UrlTree>
  | boolean
  | UrlTree => {
  const router = inject(Router);
  const sessionService = inject(SessionService);
  const companyIdSAML = safeStorageGet(SAML_LOGIN_COMPANY_ID);
  if (companyIdSAML) {
    safeStorageRemove(SAML_LOGIN_COMPANY_ID);
    return sessionService.changeCurrentCompany(companyIdSAML).pipe(
      take(1),
      map((success) => {
        if (success) {
          return router.createUrlTree([`/c/${companyIdSAML}`]);
        }
        return true;
      }),
      catchError((error) => {
        // A CanActivate observable that ERRORS becomes a NavigationError with
        // an empty router-outlet on the initial SAML return navigation (blank
        // page). Map the failure to a UrlTree so the navigation still succeeds
        // and lands on a visible error screen.
        Sentry.captureException(error, {
          tags: { flow: 'saml_redirect_guard' },
          extra: { company_id: companyIdSAML },
        });
        return of(
          router.createUrlTree(['/error'], {
            queryParams: { reason: 'saml_bootstrap' },
          })
        );
      })
    );
  }
  return true;
};
